/**
 * Colours for annotations (well markers and their labels) drawn on top of a
 * model slice image. The image itself is rendered by the backend with the
 * palette the user picked, so a single hardcoded colour can't stay legible:
 * black markers disappear into the dark end of a greyscale image.
 */
export interface PaletteContrast {
  /** Stroke / fill for the marker line and label. */
  marker: string;
  /** Halo painted behind the label so it survives both ends of the palette. */
  halo: string;
}

/** Palettes with no hue of their own - an accent colour is the only thing that stands out. */
const MONOCHROME_PALETTE = /grey|gray|binary|bone|mono/i;

/** Palettes that are mostly dark, where black annotations get lost. */
const DARK_PALETTE =
  /viridis|magma|inferno|plasma|cividis|turbo|jet|rainbow|hsv|hot|copper|cubehelix|twilight|ocean|gnuplot|nipy_spectral/i;

/**
 * Picks the annotation colour for a palette. Anything unrecognised keeps the
 * historical black-on-light-centre look, which suits the white-centred
 * reflectivity palettes (SEGRefl*, red_white_blue) used by default.
 */
export function paletteContrast(cmap: string): PaletteContrast {
  if (!cmap) return { marker: "black", halo: "white" };

  if (MONOCHROME_PALETTE.test(cmap))
    return { marker: "#00e5ff", halo: "black" };
  if (DARK_PALETTE.test(cmap)) return { marker: "white", halo: "black" };

  return { marker: "black", halo: "white" };
}
